// PersonnelView.tsx - FIXED: Resolved all TypeScript and React warnings
import React, { useEffect, useState, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

// Enhanced interface with multiple document types (unchanged)
interface PersonnelRecord {
  personnelId: number;
  companyId: number;
  fullName: string;
  firstName?: string;
  lastName?: string;
  nationality?: string;
  passportNumber?: string;
  visaType?: string;
  dateOfBirth?: string;
  sex?: string;
  placeOfBirth?: string;
  nationalIdNo?: string;
  passportIssueDate?: string;
  passportExpiryDate?: string;
  typeOfVisa?: string;
  visaIssueDate?: string;
  visaExpiry?: string;
  bloodGroup?: string;
  photoUrl?: string;
  
  // Legacy document fields (for backward compatibility)
  documentFileName?: string;
  documentFileUrl?: string;
  documentFileSize?: number;
  documentFileType?: string;
  
  // NEW: Passport document fields
  passportDocumentFileName?: string;
  passportDocumentFileUrl?: string;
  passportDocumentFileSize?: number;
  passportDocumentFileType?: string;
  
  // NEW: Visa/ID document fields
  visaIdDocumentFileName?: string;
  visaIdDocumentFileUrl?: string;
  visaIdDocumentFileSize?: number;
  visaIdDocumentFileType?: string;
  
  createdAt: string;
  updatedAt: string;
}

interface PersonnelViewWrapperProps {
  personnelData: PersonnelRecord | null;
  onClose: () => void;
  onEdit?: (personnel: PersonnelRecord) => void;
  onDelete?: (personnel: PersonnelRecord) => void;
}

interface DocumentInfo {
  type: 'passport' | 'visaid' | 'legacy';
  label: string;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  fileType?: string;
  icon: string;
  badgeColor: string;
}

interface PreviewItem {
  kind: 'photo' | 'document';
  badgeLabel: string;
  badgeColor: string;
  label: string;
  url: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  document?: DocumentInfo;
}

export const PersonnelViewWrapper: React.FC<PersonnelViewWrapperProps> = ({
  personnelData,
  onClose,
  onEdit,
  onDelete
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sensitiveRevealed, setSensitiveRevealed] = useState(false);
  const [previewState, setPreviewState] = useState<{ items: PreviewItem[]; index: number } | null>(null);

  // FIXED: Removed unused debugInfo state
  // const [debugInfo, setDebugInfo] = useState<string>('');

  // Helper functions - FIXED: Using proper helper functions
  const constructPhotoUrl = (photoUrl: string | undefined): string | null => {
    if (!photoUrl) return null;
    
    // If it's already a complete URL (Azure Blob Storage or any HTTP URL), return as-is
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
      return photoUrl;
    }
    
    // If it starts with /api/, use as-is with base URL
    if (photoUrl.startsWith('/api/')) {
      return `${API_BASE_URL.replace('/api', '')}${photoUrl}`;
    }
    
    // If it starts with /, assume it's a path from root
    if (photoUrl.startsWith('/')) {
      return `${API_BASE_URL.replace('/api', '')}${photoUrl}`;
    }
    
    // Otherwise, assume it's a filename and construct the full path
    return `${API_BASE_URL}/uploads/photos/${photoUrl}`;
  };

  const constructDocumentUrl = (documentUrl: string | undefined): string | undefined => {
    if (!documentUrl) return undefined;
    
    // If it's already a complete URL (Azure Blob Storage or any HTTP URL), return as-is
    if (documentUrl.startsWith('http://') || documentUrl.startsWith('https://')) {
      return documentUrl;
    }
    
    // If it starts with /api/, use as-is with base URL
    if (documentUrl.startsWith('/api/')) {
      return `${API_BASE_URL.replace('/api', '')}${documentUrl}`;
    }
    
    // If it starts with /, assume it's a path from root
    if (documentUrl.startsWith('/')) {
      return `${API_BASE_URL.replace('/api', '')}${documentUrl}`;
    }
    
    // Otherwise, assume it's a filename and construct the full path
    return `${API_BASE_URL}/uploads/documents/${documentUrl}`;
  };

  // Helper: convert ALL CAPS names to Title Case
  const toTitleCase = (str: string | undefined): string | undefined => {
    if (!str) return str;
    return str
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatFileSize = (bytes: number | undefined): string => {
    if (!bytes) return 'Unknown size';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = (bytes / Math.pow(1024, i)).toFixed(1);
    
    return `${size} ${sizes[i]}`;
  };

  // FIXED: Memoized getAvailableDocuments function to fix dependency warning
  const getAvailableDocuments = useCallback((): DocumentInfo[] => {
    if (!personnelData) return [];
    
    const documents: DocumentInfo[] = [];

    // Check for passport document
    if (personnelData.passportDocumentFileName) {
      documents.push({
        type: 'passport',
        label: 'Passport Document',
        fileName: personnelData.passportDocumentFileName,
        fileUrl: personnelData.passportDocumentFileUrl,
        fileSize: personnelData.passportDocumentFileSize,
        fileType: personnelData.passportDocumentFileType,
        icon: '📘', // Fixed: Proper passport icon
        badgeColor: '#6b7280'
      });
    }

    // Check for visa/ID document
    if (personnelData.visaIdDocumentFileName) {
      documents.push({
        type: 'visaid',
        label: 'Visa/ID Document',
        fileName: personnelData.visaIdDocumentFileName,
        fileUrl: personnelData.visaIdDocumentFileUrl,
        fileSize: personnelData.visaIdDocumentFileSize,
        fileType: personnelData.visaIdDocumentFileType,
        icon: '🆔',
        badgeColor: '#6b7280'
      });
    }

    // Check for legacy document (only if no new documents exist)
    if (personnelData.documentFileName && documents.length === 0) {
      documents.push({
        type: 'legacy',
        label: 'Document',
        fileName: personnelData.documentFileName,
        fileUrl: personnelData.documentFileUrl,
        fileSize: personnelData.documentFileSize,
        fileType: personnelData.documentFileType,
        icon: '📄',
        badgeColor: '#6b7280'
      });
    }

    return documents;
  }, [personnelData]);

  // Function to construct download URL for different document types
  const getDocumentDownloadUrl = (document: DocumentInfo): string => {
    if (document.fileUrl) {
      return constructDocumentUrl(document.fileUrl) || '#';
    }
    
    // If no direct URL, use the download endpoint
    if (document.type === 'legacy') {
      return `${API_BASE_URL}/personnel/${personnelData?.personnelId}/file/document`;
    }
    
    return `${API_BASE_URL}/personnel/${personnelData?.personnelId}/file/${document.type}`;
  };

  const buildPreviewItems = (): PreviewItem[] => {
    const items: PreviewItem[] = [];
    const photoUrl = personnelData?.photoUrl ? constructPhotoUrl(personnelData.photoUrl) : null;
    if (photoUrl) {
      items.push({
        kind: 'photo',
        badgeLabel: 'PHOTO',
        badgeColor: '#6b7280',
        label: 'Profile Photo',
        url: photoUrl,
        fileType: 'image/jpeg',
      });
    }
    for (const doc of getAvailableDocuments()) {
      items.push({
        kind: 'document',
        badgeLabel: doc.type === 'visaid' ? 'VISA / ID' : doc.type.toUpperCase(),
        badgeColor: doc.badgeColor,
        label: doc.label,
        url: getDocumentDownloadUrl(doc),
        fileName: doc.fileName,
        fileSize: doc.fileSize,
        fileType: doc.fileType,
        document: doc,
      });
    }
    return items;
  };

  // Helper function to format date for display
  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'Not provided';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to format date for input display
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  // FIXED: Single renderReadOnlyField function declaration
  const renderReadOnlyField = (
    label: string, 
    value: string | undefined, 
    type: 'text' | 'date' | 'select' = 'text',
    options?: string[],
    masked?: boolean,
    nameProtected?: boolean
  ) => {
    const displayValue = type === 'date' ? formatDateForInput(value) : (value || '');
    const isEmpty = !displayValue || displayValue.trim() === '';
    const maskedValue = masked && !isEmpty ? '•'.repeat(displayValue.length) : displayValue;
    const nameHidden = nameProtected && sensitiveRevealed && !isEmpty;

    return (
      <div>
        <label style={{
          display: 'block',
          marginBottom: '6px',
          fontWeight: '500',
          color: '#374151',
          fontSize: '13px',
          fontFamily: "'Segoe UI', sans-serif",
        }}>
          {label}
        </label>
        <div
          onMouseDown={(e) => {
            if (masked && !isEmpty) {
              const el = e.currentTarget;
              el.textContent = displayValue;
              el.style.cursor = 'text';
              setSensitiveRevealed(true);
            }
          }}
          onMouseUp={(e) => {
            if (masked && !isEmpty) {
              const el = e.currentTarget;
              el.textContent = maskedValue;
              el.style.cursor = 'pointer';
              setSensitiveRevealed(false);
            }
          }}
          onMouseLeave={(e) => {
            if (masked && !isEmpty) {
              const el = e.currentTarget;
              el.textContent = maskedValue;
              el.style.cursor = 'pointer';
              setSensitiveRevealed(false);
            }
          }}
          title={masked && !isEmpty ? 'Press and hold to reveal' : undefined}
          style={{
            width: '100%',
            padding: '10px 14px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '14px',
            backgroundColor: '#f9fafb',
            color: isEmpty ? '#9ca3af' : '#111827',
            height: '42px',
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'Segoe UI', sans-serif",
            boxSizing: 'border-box' as const,
            cursor: masked && !isEmpty ? 'pointer' : 'default',
            userSelect: 'none' as const,
          }}
        >
          {isEmpty ? 'Not provided' : nameHidden ? '•'.repeat(displayValue.length) : maskedValue}
        </div>
      </div>
    );
  };

  const renderDocumentsSection = () => {
    const availableDocuments = getAvailableDocuments();

    // SVG icon for a generic document file
    const DocFileIcon = () => (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <line x1="10" y1="9" x2="8" y2="9" />
      </svg>
    );

    // SVG icon for download arrow
    const DownloadIcon = () => (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    );

    const sectionHeader = (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #f1f5f9',
      }}>
        <h4 style={{
          margin: 0,
          color: '#111827',
          fontSize: '16px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          Attached Documents
        </h4>
        <span style={{
          fontSize: '12px',
          color: '#6b7280',
          fontWeight: '500',
          backgroundColor: '#f3f4f6',
          border: '1px solid #e5e7eb',
          padding: '2px 10px',
          borderRadius: '20px',
          letterSpacing: '0.01em',
        }}>
          {availableDocuments.length === 0
            ? 'No documents'
            : `${availableDocuments.length} ${availableDocuments.length === 1 ? 'document' : 'documents'}`}
        </span>
      </div>
    );

    if (availableDocuments.length === 0) {
      return (
        <div style={{ marginBottom: '32px' }}>
          {sectionHeader}
          <div style={{
            padding: '20px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>No documents attached to this personnel record.</span>
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: '32px' }}>
        {sectionHeader}
        <div style={{
          display: 'grid',
          gridTemplateColumns: availableDocuments.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '12px',
        }}>
          {availableDocuments.map((document, index) => (
            <div key={`${document.type}-${index}`} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '14px 16px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
            }}>
              {/* File type icon container */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '6px',
                backgroundColor: '#f3f4f6',
                border: '1px solid #e5e7eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <DocFileIcon />
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px', flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#111827',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '160px',
                  }}>
                    {document.fileName}
                  </span>
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #e5e7eb',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    textTransform: 'uppercase',
                    fontWeight: '600',
                    letterSpacing: '0.04em',
                    whiteSpace: 'nowrap',
                  }}>
                    {document.type === 'visaid' ? 'VISA / ID' : document.type.toUpperCase()}
                  </span>
                </div>
                <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' }}>
                  {document.fileType} &nbsp;·&nbsp; {formatFileSize(document.fileSize)}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    const items = buildPreviewItems();
                    const idx = items.findIndex(it => it.kind === 'document' && it.document?.type === document.type);
                    setPreviewState({ items, index: idx >= 0 ? idx : 0 });
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '5px 11px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#F3694A';
                    e.currentTarget.style.color = '#F3694A';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.color = '#374151';
                  }}
                >
                  <DownloadIcon />
                  View / Download
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // FIXED: Enhanced debug logging - now includes getAvailableDocuments in dependency array
  useEffect(() => {
    if (personnelData) {
      console.log('=== ENHANCED PERSONNEL DEBUG ===');
      console.log('Full personnel data:', personnelData);
      console.log('photoUrl field:', personnelData.photoUrl);
      console.log('photoUrl type:', typeof personnelData.photoUrl);
      console.log('photoUrl length:', personnelData.photoUrl?.length);
      
      // Enhanced document debugging
      console.log('=== DOCUMENT DEBUG ===');
      console.log('Legacy document:', {
        fileName: personnelData.documentFileName,
        fileUrl: personnelData.documentFileUrl,
        fileSize: personnelData.documentFileSize,
        fileType: personnelData.documentFileType
      });
      
      console.log('Passport document:', {
        fileName: personnelData.passportDocumentFileName,
        fileUrl: personnelData.passportDocumentFileUrl,
        fileSize: personnelData.passportDocumentFileSize,
        fileType: personnelData.passportDocumentFileType
      });
      
      console.log('Visa/ID document:', {
        fileName: personnelData.visaIdDocumentFileName,
        fileUrl: personnelData.visaIdDocumentFileUrl,
        fileSize: personnelData.visaIdDocumentFileSize,
        fileType: personnelData.visaIdDocumentFileType
      });
      
      const availableDocuments = getAvailableDocuments();
      console.log('Available documents:', availableDocuments);
      console.log('========================');
    }
  }, [personnelData, getAvailableDocuments]); // FIXED: Added getAvailableDocuments to dependency array
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    return () => {};
  }, []);

  if (!personnelData) {
    return (
      <div style={{ 
        padding: '60px 24px', 
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
      }}>
        <div>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ color: '#374151', margin: '0 0 8px 0' }}>Personnel Data Not Found</h3>
          <p style={{ color: '#6b7280', margin: 0 }}>Unable to load personnel details.</p>
        </div>
      </div>
    );
  }

  // FIXED: Simplified photo component with proper Unicode characters
  const PhotoComponent = () => {
    const [photoUrl, setPhotoUrl] = useState<string | null>(null);
    const [photoLoadError, setPhotoLoadError] = useState(false);
    
    // Extract the specific value we need to track
    const currentPhotoUrl = personnelData?.photoUrl;
    
    useEffect(() => {
      if (currentPhotoUrl) {
        const constructedUrl = constructPhotoUrl(currentPhotoUrl);
        setPhotoUrl(constructedUrl);
        setPhotoLoadError(false);
        console.log('Using photo URL:', constructedUrl);
      } else {
        setPhotoUrl(null);
        setPhotoLoadError(false);
      }
    }, [currentPhotoUrl]); // Now we depend on the specific extracted value
    
    const handlePhotoError = () => {
      console.log('Photo failed to load:', photoUrl);
      setPhotoLoadError(true);
    };

    const hasPhoto = !!photoUrl && !photoLoadError;

    return (
      <div
        onClick={hasPhoto ? () => {
          const items = buildPreviewItems();
          const photoIdx = items.findIndex(it => it.kind === 'photo');
          if (photoIdx >= 0) setPreviewState({ items, index: photoIdx });
        } : undefined}
        style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          backgroundColor: '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '3px solid #e5e7eb',
          overflow: 'hidden',
          position: 'relative',
          flexShrink: 0,
          cursor: hasPhoto ? 'pointer' : 'default',
        }}>
        {photoUrl && !photoLoadError ? (
          <>
            <img 
              src={photoUrl} 
              alt={`${toTitleCase(personnelData.fullName)} profile`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={handlePhotoError}
              onLoad={() => console.log('Photo loaded successfully:', photoUrl)}
            />
            {/* Success indicator - FIXED: Proper Unicode checkmark */}
            <div 
              title={`Photo loaded from: ${photoUrl}`}
              style={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: '12px',
                height: '12px',
                backgroundColor: '#10b981',
                borderRadius: '50%',
                fontSize: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              ✓
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '40px',
              color: '#9ca3af',
              marginBottom: '4px'
            }}>
              👤
            </div>
            {personnelData?.photoUrl && photoLoadError && (
              <div 
                title={`Photo URL failed to load: ${photoUrl}`}
                style={{
                  fontSize: '8px',
                  color: '#ef4444',
                  fontWeight: 'bold'
                }}
              >
                ⚠️
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const DocumentPreviewModal = () => {
    if (!previewState) return null;
    const { items, index } = previewState;
    const item = items[index];
    const isImage = item.kind === 'photo' || item.fileType?.startsWith('image/') || /\.(png|jpg|jpeg|gif|bmp|webp)$/i.test(item.fileName || '');
    const isPdf = item.fileType === 'application/pdf' || /\.pdf$/i.test(item.fileName || '');
    const canDownload = item.kind === 'document';

    const goTo = (i: number) => setPreviewState({ items, index: (i + items.length) % items.length });

    const NavArrow = ({ direction }: { direction: 'prev' | 'next' }) => (
      <button
        type="button"
        onClick={() => goTo(direction === 'prev' ? index - 1 : index + 1)}
        aria-label={direction === 'prev' ? 'Previous' : 'Next'}
        style={{
          position: 'absolute', top: '50%', transform: 'translateY(-50%)',
          [direction === 'prev' ? 'left' : 'right']: '12px',
          width: '36px', height: '36px', borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.9)', border: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          transition: 'all 0.15s', zIndex: 1,
          color: '#374151',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'; }}
      >
        {direction === 'prev'
          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        }
      </button>
    );

    return (
      <div
        onClick={(e) => { if (e.target === e.currentTarget) setPreviewState(null); }}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.75)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div style={{
          backgroundColor: 'white', borderRadius: '12px',
          display: 'flex', flexDirection: 'column',
          width: '100%', maxWidth: '860px',
          maxHeight: '90vh',
          boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{
                fontSize: '10px', fontWeight: '700', color: 'white',
                backgroundColor: item.badgeColor, padding: '2px 8px',
                borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                {item.badgeLabel}
              </span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#111827', maxWidth: '460px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.fileName || item.label}
              </span>
              {item.fileSize && <span style={{ fontSize: '12px', color: '#9ca3af' }}>{formatFileSize(item.fileSize)}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {canDownload && (
                <a
                  href={item.url}
                  download={item.fileName}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '7px 14px', fontSize: '13px', fontWeight: '600',
                    color: 'white', backgroundColor: '#F3694A', border: '1px solid #F3694A',
                    borderRadius: '6px', textDecoration: 'none', cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#e05a3d'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#e05a3d'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#F3694A'; (e.currentTarget as HTMLAnchorElement).style.borderColor = '#F3694A'; }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Download
                </a>
              )}
              <button
                type="button"
                onClick={() => setPreviewState(null)}
                aria-label="Close preview"
                style={{
                  background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px',
                  fontSize: '18px', cursor: 'pointer', color: '#6b7280',
                  width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.borderColor = '#9ca3af'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
              >
                ✕
              </button>
            </div>
          </div>

          {/* Document viewer with nav arrows */}
          <div style={{ position: 'relative', flex: 1, overflow: 'auto', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, padding: '16px' }}>
            {items.length > 1 && <NavArrow direction="prev" />}
            {isImage ? (
              <img
                key={item.url}
                src={item.url}
                alt={item.fileName || item.label}
                style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
              />
            ) : isPdf ? (
              <iframe
                key={item.url}
                src={item.url}
                title={item.fileName || item.label}
                style={{ width: '100%', height: '60vh', border: 'none', borderRadius: '4px' }}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
                <p style={{ color: '#374151', fontWeight: '600', marginBottom: '8px' }}>{item.fileName}</p>
                <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '20px' }}>Preview not available for this file type.</p>
                <a href={item.url} download={item.fileName} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 18px', fontSize: '13px', fontWeight: '600', color: 'white', backgroundColor: '#F3694A', borderRadius: '6px', textDecoration: 'none' }}>
                  Download to view
                </a>
              </div>
            )}
            {items.length > 1 && <NavArrow direction="next" />}
          </div>

          {/* Dot indicators */}
          {items.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '12px 0', backgroundColor: 'white', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
              {items.map((it, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => goTo(i)}
                  aria-label={it.label}
                  title={it.label}
                  style={{
                    width: i === index ? '20px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    backgroundColor: i === index ? '#F3694A' : '#d1d5db',
                    transition: 'all 0.2s ease',
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Modal Header - FIXED: Proper Unicode close button */}
      <div 
        style={{
          padding: '24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'white'
        }}
      >
        <div>
          <h2 
            style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: '600',
              color: '#111827'
            }}
          >
            Personnel Details
          </h2>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: '14px',
            color: '#6b7280'
          }}>
            Viewing information for {sensitiveRevealed ? '•'.repeat((toTitleCase(personnelData.fullName) || '').length) : toTitleCase(personnelData.fullName)}
          </p>
        </div>
        <button 
          type="button" 
          onClick={onClose}
          aria-label="Close modal"
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px',
            borderRadius: '4px',
            transition: 'color 0.2s'
          }}
        >
          ✕
        </button>
      </div>

      {/* Modal Body */}
      <div style={{ 
        flex: 1, 
        padding: 0, 
        overflow: 'hidden', 
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0
      }}>
        <div style={{ 
          padding: '32px', 
          overflowY: 'scroll',
          maxHeight: 'calc(100vh - 160px)',
          WebkitOverflowScrolling: 'touch',
        }}>
         
          
          {/* Profile Photo Section */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '24px', 
            marginBottom: '32px',
            paddingBottom: '24px',
            borderBottom: '1px solid #f1f5f9'
          }}>
            {/* Enhanced Profile Picture with Debug */}
            <PhotoComponent />
            
            {/* Profile Info */}
            <div style={{ flex: 1 }}>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '600',
                color: '#111827'
              }}>
                {(() => {
                  const nameDisplay = [toTitleCase(personnelData.firstName), toTitleCase(personnelData.lastName)].filter(Boolean).join(' ') || toTitleCase(personnelData.fullName) || '';
                  return sensitiveRevealed ? '•'.repeat(nameDisplay.length) : nameDisplay;
                })()}
              </h3>
              <p style={{
                margin: '0 0 4px 0',
                fontSize: '14px',
                color: '#6b7280'
              }}>
                
              </p>
              <p style={{
                margin: 0,
                fontSize: '13px',
                color: '#9ca3af'
              }}>
                Added on {formatDate(personnelData.createdAt)}
                {personnelData.updatedAt !== personnelData.createdAt && (
                  <span> • Last updated {formatDate(personnelData.updatedAt)}</span>
                )}
              </p>
              
            </div>
          </div>

          {/* Personal Info Section */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ 
              margin: '0 0 16px 0', 
              color: '#111827',
              fontSize: '16px',
              fontWeight: '600',
              paddingBottom: '12px',
              borderBottom: '2px solid #f1f5f9'
            }}>
              Personal Information
            </h4>
            
            {/* First Name and Last Name Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderReadOnlyField('First Name', toTitleCase(personnelData.firstName), 'text', undefined, false, true)}
              {renderReadOnlyField('Last Name', toTitleCase(personnelData.lastName), 'text', undefined, false, true)}
            </div>

            {/* Gender and Nationality Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderReadOnlyField('Gender', personnelData.sex)}
              {renderReadOnlyField('Nationality', personnelData.nationality)}
            </div>

            {/* Date of Birth and Place of Birth Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderReadOnlyField('Date of Birth', personnelData.dateOfBirth, 'date', undefined, true)}
              {renderReadOnlyField('Place of Birth', toTitleCase(personnelData.placeOfBirth))}
            </div>

            {/* Blood Group Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {renderReadOnlyField('Blood Group', personnelData.bloodGroup)}
              <div>{/* Empty cell for alignment */}</div>
            </div>
          </div>

          {/* Identity Documents Section */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ 
              margin: '0 0 16px 0', 
              color: '#111827',
              fontSize: '16px',
              fontWeight: '600',
              paddingBottom: '12px',
              borderBottom: '2px solid #f1f5f9'
            }}>
              Identity Documents
            </h4>
            
            {/* National ID and Passport Number Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderReadOnlyField('National ID Number', personnelData.nationalIdNo, 'text', undefined, true)}
              {renderReadOnlyField('Passport Number', personnelData.passportNumber, 'text', undefined, true)}
            </div>

            {/* Passport Issue and Expiry Date Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {renderReadOnlyField('Passport Issue Date', personnelData.passportIssueDate, 'date')}
              {renderReadOnlyField('Passport Expiry Date', personnelData.passportExpiryDate, 'date')}
            </div>
          </div>

          {/* Visa Information Section */}
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{ 
              margin: '0 0 16px 0', 
              color: '#111827',
              fontSize: '16px',
              fontWeight: '600',
              paddingBottom: '12px',
              borderBottom: '2px solid #f1f5f9'
            }}>
              Visa Information
            </h4>
            
            {/* Visa Type and Issue Date Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '16px' }}>
              {renderReadOnlyField('Visa Type', personnelData.typeOfVisa)}
              {renderReadOnlyField('Visa Issue Date', personnelData.visaIssueDate, 'date')}
            </div>

            {/* Visa Expiry Date */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {renderReadOnlyField('Visa Expiry Date', personnelData.visaExpiry, 'date')}
              <div>{/* Empty cell for alignment */}</div>
            </div>
          </div>

          {/* MOVED: Enhanced Documents Section - Now at the bottom before System Information */}
          {renderDocumentsSection()}

          {/* System Information - UPDATED: Removed document summary since it's now above */}
          <div style={{
            marginTop: '0px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <h4 style={{ 
              margin: '0 0 16px 0', 
              color: '#6b7280',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              System Information
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', fontSize: '13px' }}>
              <div style={{ paddingBottom: '8px' }}>
                <span style={{ fontWeight: '500', color: '#374151' }}>Created:</span>
                <span style={{ marginLeft: '8px', color: '#6b7280' }}>{formatDate(personnelData.createdAt)}</span>
              </div>
              <div style={{ paddingBottom: '8px' }}>
                <span style={{ fontWeight: '500', color: '#374151' }}>Last Updated:</span>
                <span style={{ marginLeft: '8px', color: '#6b7280' }}>{formatDate(personnelData.updatedAt)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer — action buttons */}
        <div style={{
          padding: '16px 32px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          backgroundColor: '#fafafa',
          flexShrink: 0
        }}>
          {/* Left: Delete */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {onDelete && personnelData && !showDeleteConfirm && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                style={{
                  height: '36px', padding: '0 12px', borderRadius: '6px',
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  background: 'none', color: '#9ca3af', border: '1px solid transparent',
                  fontFamily: "'Segoe UI', sans-serif", transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fecaca'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                Delete
              </button>
            )}
            {showDeleteConfirm && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', animation: 'vdmFadeIn 0.15s ease' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#dc2626' }}>Delete this record?</span>
                <button
                  type="button"
                  onClick={() => { if (onDelete && personnelData) { onClose(); onDelete(personnelData); } }}
                  style={{
                    height: '32px', padding: '0 14px', borderRadius: '6px',
                    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: '#dc2626', color: '#ffffff', border: '1px solid #dc2626',
                    fontFamily: "'Segoe UI', sans-serif", transition: 'all 0.15s ease'
                  }}
                >
                  Yes, Delete
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    height: '32px', padding: '0 14px', borderRadius: '6px',
                    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: '#ffffff', color: '#6b7280', border: '1px solid #d1d5db',
                    fontFamily: "'Segoe UI', sans-serif", transition: 'all 0.15s ease'
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* Right: Edit + Close */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {onEdit && personnelData && (
              <button
                type="button"
                onClick={() => { onClose(); onEdit(personnelData); }}
                style={{
                  height: '36px', padding: '0 16px', borderRadius: '6px',
                  fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  background: '#F3694A', color: '#ffffff', border: '1px solid #F3694A',
                  fontFamily: "'Segoe UI', sans-serif", transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#e05a3d'; e.currentTarget.style.borderColor = '#e05a3d'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#F3694A'; e.currentTarget.style.borderColor = '#F3694A'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
                Edit
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                height: '36px', padding: '0 16px', borderRadius: '6px',
                fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: '#ffffff', color: '#374151', border: '1px solid #d1d5db',
                fontFamily: "'Segoe UI', sans-serif", transition: 'all 0.15s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#9ca3af'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.borderColor = '#d1d5db'; }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      <DocumentPreviewModal />
    </div>
  );
};

export default PersonnelViewWrapper;